<template>
    <div>
        <AdminBlock title="Split Movement">
            <div class="row">
                <div class="col-6">
                    <EstimateSplitRoute
                        :delivery="{
                          lat: movement.delivery_lat,
                          lng: movement.delivery_lng,
                        }"
                        :split="{
                          lat: form.splitLatitude,
                          lng: form.splitLongitude,
                        }"
                        :collection="{
                          lat: movement.collection_lat,
                          lng: movement.collection_lng,
                        }"
                        @update-mileage="updateMileage"
                    />
                </div>

                <div class="col-6">
                    <div class="mb-4">
                        <label>
                            <strong>First Leg End Date</strong>
                        </label>

                        <p class="mb-0">
                            <small>This is the Delivery Date of the Movement being split</small>
                        </p>

                        <div class="input-group datetime">
                            <span class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </span>
                            <input
                                class="form-control js-flatpickers-movements"
                                placeholder="yyyy-mm-dd"
                                v-model="form.firstLegEndDate"
                                type="date"
                                ref="firstLegEndDateEl"
                            />
                        </div>
                    </div>

                    <JobTime
                        title="First Leg End Time"
                        subtitle="This is the Delivery Time of the Movement being split"
                        @update-time="time => { form.firstLegEndTime = time }"
                        remove-margin
                    />

                    <hr/>

                    <div class="mb-4">
                        <label>
                            <strong>Second Leg Collection Date</strong>
                        </label>
                        <p class="mb-0">
                            <small>This is the collection date of the new Movement</small>
                        </p>

                        <div class="input-group datetime">
                            <span class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </span>
                            <input
                                class="form-control js-flatpickers-movements"
                                placeholder="yyyy-mm-dd"
                                v-model="form.secondLegStartDate"
                                type="date"
                                ref="secondLegStartDateEl"
                            />
                        </div>
                    </div>

                    <JobTime
                        title="Second Leg Start Time"
                        subtitle="This is the Collection Time of the new Movement"
                        @update-time="time => { form.secondLegStartTime = time }"
                        remove-margin
                    />

                    <hr/>

                    <div class="mb-4">
                        <label>
                            <strong>Second Leg Delivery Date</strong>
                        </label>

                        <p class="mb-0">
                            <small>This is also the Job Delivery Date if this is last movement</small>
                        </p>

                        <div class="input-group datetime">
                              <span class="input-group-text">
                                <i class="fa fa-calendar"></i>
                              </span>

                            <input
                                class="form-control js-flatpickers-movements"
                                placeholder="yyyy-mm-dd"
                                v-model="form.newDeliveryDate"
                                type="date"
                                ref="newDeliveryDateEl"
                            />
                        </div>
                    </div>

                    <JobTime
                        title="New Delivery Time"
                        subtitle="This is the Job Delivery Time if this is last movement"
                        @update-time="time => { form.newDeliveryTime = time }"
                        remove-margin
                    />

                    <hr/>

                    <div class="mb-4">
                        <label>
                            <strong>Split Company</strong>
                        </label>

                        <p class="mb-0">
                            <small>Changes the Company for the Second Leg if different from the first</small>
                        </p>

                        <input
                            type="text"
                            v-model="form.splitCompany"
                            class="form-control"
                            placeholder="Name of Company if different from Customer"
                        />
                    </div>

                    <hr/>

                    <label>
                        <strong>Split Location</strong>
                    </label>

                    <p class="mb-0">
                        <small>
                            This is the location where the initial Movement ends and the new Movement starts.
                        </small>
                    </p>

                    <GoogleMap
                        :location="form.splitLocation"
                        :selected-customer="movement.job.customer"
                        jk-lookup-enabled
                        @location-changed="updateSplitLocation"
                    />
                </div>
            </div>
        </AdminBlock>

        <AdminBlock>
            <div class="d-flex justify-content-end">
                <a :href="jobRoute()" class="btn btn-secondary me-2">
                    Back to Job {{ movement.job.reference }}
                </a>
                <button type="button" class="btn btn-success" @click="sendFormRequest" :disabled="isLoading">
                    Save
                </button>
            </div>
        </AdminBlock>
    </div>
</template>

<script setup>

import {reactive, ref, onMounted } from 'vue'
import axios from 'axios'
import GoogleMap from '@/components/movements/components/GoogleMap.vue'
import EstimateSplitRoute from '@/components/movements/components/EstimateSplitRoute.vue'
import JobTime from '@/components/movements/components/JobTime.vue'
import AdminBlock from '@/components/movements/components/AdminBlock.vue'
import {errorAlert, successAlert} from '@//utils'

const DEFAULT_SPLIT_LOCATION = ''

const props = defineProps({
    formattedMovement: {
        type: Object,
        required: true
    },
    movement: {
        type: Object,
        required: true
    },
})

const form = reactive({
    firstLegEndDate: new Date(props.movement.delivery_date).toISOString().slice(0, 10),
    firstLegEndTime: props.movement.delivery_time,
    secondLegStartDate: props.formattedMovement.delivery_date_next_day,
    secondLegStartTime: 'Any',
    splitLocation: DEFAULT_SPLIT_LOCATION,
    splitLocale: null,
    splitLatitude: null,
    splitLongitude: null,
    splitCompany: '',
    splitMileageLegOne: 0,
    splitMileageLegTwo: 0,
    newDeliveryDate: props.formattedMovement.delivery_date_next_day,
    newDeliveryTime: 'Any',
})

const isLoading = ref(false)

const firstLegEndDateEl = ref(null)
const secondLegStartDateEl = ref(null)
const newDeliveryDateEl = ref(null)

function jobRoute() {
    return route('movements.jobs.show', { job: props.movement.job.id })
}

onMounted(() => {
    if (typeof flatpickr !== 'undefined') {
        flatpickr('.js-flatpickers-movements', {
            altInput: true,
            altFormat: 'F j, Y',
            dateFormat: 'Y-m-d',
        })

        // set initial dates on the specific inputs
        firstLegEndDateEl.value?._flatpickr?.setDate(props.formattedMovement.delivery_date)
        secondLegStartDateEl.value?._flatpickr?.setDate(props.formattedMovement.delivery_date_next_day)
        newDeliveryDateEl.value?._flatpickr?.setDate(props.formattedMovement.delivery_date_next_day)
    }
})

function sendFormRequest() {
    if (isLoading.value) return
    isLoading.value = true

    const data = {
        movement_id: props.movement.id,
        first_leg_end_date: form.firstLegEndDate,
        first_leg_end_time: form.firstLegEndTime,
        second_leg_start_date: form.secondLegStartDate,
        second_leg_start_time: form.secondLegStartTime,
        split_lat: form.splitLatitude,
        split_lng: form.splitLongitude,
        split_location: form.splitLocation,
        split_locale: form.splitLocale,
        split_company: form.splitCompany,
        split_mileage_leg_one: form.splitMileageLegOne,
        split_mileage_leg_two: form.splitMileageLegTwo,
        new_delivery_date: form.newDeliveryDate,
        new_delivery_time: form.newDeliveryTime,
    }

    axios
        .post(route('api.movements.movements.split', {movement: props.movement.id}), data)
        .then((response) => {
            const message = 'Split Movement ' + props.movement.position + ' for ' + props.movement.job.reference
            successAlert(message, 'Success!', 2000, true).then(() => {
                window.location.href = route('movements.movements.show', { movement: response.data.movement_id })
            })
        })
        .catch((error) => {
            errorAlert(error)
        })
        .finally(() => {
            isLoading.value = false
        })
}

function updateMileage(value) {
    form.splitMileageLegOne = value.legOne
    form.splitMileageLegTwo = value.legTwo
}

function updateSplitLocation(value) {
    form.splitLatitude = value.latitude
    form.splitLongitude = value.longitude
    form.splitLocation = value.location
    form.splitLocale = value.locale
}
</script>
